import { useState } from 'react';

import { Card } from '../../components/ui/Card';
import { Character } from '../character/Character';
import {
  DEFAULT,
  OPTIONS,
  PALETTE,
  fromSeed,
  type Character as CharacterConfig,
} from '../character/config';

/**
 * The character rig, with every control on it.
 *
 * ## Why this is not the maker people use
 *
 * The one in settings is deliberately small: it opens on the character somebody
 * already has, offers the parts, and gets out of the way. This is the workbench
 * behind it - both views at once, every animation, an arbitrary size, and a
 * contact sheet of every part drawn against one body.
 *
 * Adding a part means a path in `Character.tsx`, a flag in `config.ts` and a
 * name in an option list. The hard bit is not the wiring, it is finding out
 * whether the thing sits right on the head - and doing that by making an
 * account and clicking through nineteen hairstyles is slow enough that nobody
 * does it properly, so the ones that look wrong ship.
 *
 * ## Front and back
 *
 * The generator draws both off one 220x272 skeleton, and the back is where half
 * the parts actually live - a ponytail, a bun, locs, the hood of a hoodie, a
 * cap's strap. Building those against the front view alone is guesswork.
 *
 * ## Admin, and not because it is dangerous
 *
 * Nothing here writes anything. It is gated because it is a tool for the people
 * building the thing, not something to leave in the public navigation for
 * somebody to find while looking for their settings.
 */
export function AdminCharactersPage() {
  const [draft, setDraft] = useState<CharacterConfig>(DEFAULT);
  const [size, setSize] = useState(240);
  const [anim, setAnim] = useState('idle');
  const [sheets, setSheets] = useState(false);

  const set = (patch: Partial<CharacterConfig>) =>
    setDraft((current) => ({ ...current, ...patch }));

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold tracking-tight text-ink">Character rig</h1>
        <p className="text-sm text-soft">
          Front and back off one 220×272 skeleton. A new part is a path in{' '}
          <code className="font-mono text-xs text-faint">Character.tsx</code>, a flag in{' '}
          <code className="font-mono text-xs text-faint">config.ts</code> and a name in an
          option list — this is where you find out whether it sits right.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[auto_1fr] lg:items-start">
        {/* ── The two views, and what they are doing ─────────────────────── */}
        <Card className="flex flex-col gap-4 p-5 lg:sticky lg:top-4">
          <div className="flex items-end gap-5">
            {(['front', 'back'] as const).map((view) => (
              <div key={view} className="flex flex-col items-center gap-2" data-view={view}>
                <Character config={draft} size={size} crop="full" view={view} anim={anim} />
                <span className="meta-caps text-faint">{view}</span>
              </div>
            ))}
          </div>

          <label className="flex items-center gap-2">
            <span className="meta-caps text-faint">Size</span>
            <input
              type="range"
              min={60}
              max={320}
              step={4}
              value={size}
              data-rig-size
              onChange={(event) => setSize(Number(event.target.value))}
              className="flex-1"
            />
            <span className="w-12 font-mono text-[11px] text-soft">{size}px</span>
          </label>

          <div className="flex flex-wrap items-center gap-1.5">
            {/*
              Every animation the stylesheet defines, including the two that are
              states rather than gestures. The reaction endpoint offers a
              narrower list - it will not broadcast `idle` - and this is the rig,
              not the product.
            */}
            {['idle', 'nod', 'bob', 'sway', 'dance', 'clap', 'pump', 'hooray', 'wave', 'lighter', 'mosh'].map(
              (option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={anim === option}
                  data-rig-anim={option}
                  onClick={() => setAnim(option)}
                  className={`meta rounded-[var(--radius-pill)] border px-2 py-0.5 transition-colors ${
                    anim === option
                      ? 'border-transparent bg-accent text-accent-ink'
                      : 'border-line text-soft hover:border-line-strong hover:text-ink'
                  }`}
                >
                  {option}
                </button>
              ),
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              data-rig-random
              onClick={() => setDraft(fromSeed(`${Math.random()}`))}
              className="meta flex-1 rounded-[var(--radius-pill)] border border-line px-3 py-1.5 text-soft transition-colors hover:border-line-strong hover:text-ink"
            >
              Randomise
            </button>
            <button
              type="button"
              data-rig-reset
              onClick={() => setDraft(DEFAULT)}
              className="meta flex-1 rounded-[var(--radius-pill)] border border-line px-3 py-1.5 text-soft transition-colors hover:border-line-strong hover:text-ink"
            >
              Reset
            </button>
          </div>

          {/*
            The config, copyable. The generator ends with "Copy character JSON"
            for the same reason: a character worth keeping is worth being able
            to paste into a seed, a fixture or a bug report.
          */}
          <textarea
            readOnly
            data-rig-json
            value={JSON.stringify(draft, null, 2)}
            rows={6}
            className="w-full resize-none rounded-[var(--radius-control)] border border-line bg-app p-2 font-mono text-[10px] leading-relaxed text-soft"
          />
        </Card>

        {/* ── The parts ───────────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-4">
          {(
            [
              ['expr', 'Expression'],
              ['hair', 'Hair'],
              ['face', 'Facial hair'],
              ['outfit', 'Top'],
              ['legs', 'Bottom'],
              ['acc', 'On the head'],
              ['item', 'Holding'],
            ] as const
          ).map(([key, label]) => (
            <Picker
              key={key}
              label={label}
              options={OPTIONS[key]}
              value={draft[key]}
              onPick={(value) => set({ [key]: value } as Partial<CharacterConfig>)}
            />
          ))}

          {(
            [
              ['skinColor', 'Skin'],
              ['hairColor', 'Hair'],
              ['eyeColor', 'Eyes'],
              ['outfitColor', 'Clothes'],
              ['shoeColor', 'Shoes'],
            ] as const
          ).map(([key, label]) => (
            <Swatches
              key={key}
              label={label}
              colours={PALETTE[key]}
              value={draft[key]}
              onPick={(value) => set({ [key]: value } as Partial<CharacterConfig>)}
            />
          ))}

          <div className="flex flex-col gap-1.5">
            <span className="meta-caps text-faint">Extras</span>
            <div className="flex flex-wrap gap-1.5">
              {([['wrist', 'Wristband'], ['chain', 'Chain'], ['ear', 'Earrings']] as const).map(
                ([key, label]) => (
                  <Chip
                    key={key}
                    label={label}
                    on={draft[key]}
                    attr={key}
                    onPick={() => set({ [key]: !draft[key] } as Partial<CharacterConfig>)}
                  />
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      {/*
        The contact sheet, folded away.

        Every part against one fixed body is the right way to spot a hat two
        pixels too high, and it is two hundred characters tall - so it is a
        thing you open when you are checking a part rather than something the
        page opens with.
      */}
      <div className="flex flex-col gap-4">
        <button
          type="button"
          data-rig-sheets
          aria-expanded={sheets}
          onClick={() => setSheets((open) => !open)}
          className="meta-caps self-start rounded-[var(--radius-pill)] border border-line px-3 py-1.5 text-soft transition-colors hover:border-line-strong hover:text-ink"
        >
          {sheets ? 'Hide every part' : 'Show every part'}
        </button>

        {sheets &&
          (
            [
              ['hair', 'Hair'],
              ['face', 'Facial hair'],
              ['expr', 'Expressions'],
              ['outfit', 'Tops'],
              ['legs', 'Bottoms'],
              ['acc', 'On the head'],
              ['item', 'Holding'],
            ] as const
          ).map(([key, label]) => (
            <Sheet
              key={key}
              label={label}
              anim={anim}
              items={OPTIONS[key].map(([option, name]) => ({
                name,
                config: { ...draft, [key]: option } as CharacterConfig,
              }))}
            />
          ))}
      </div>
    </div>
  );
}

function Picker({
  label,
  options,
  value,
  onPick,
}: {
  label: string;
  options: readonly (readonly [string, string])[];
  value: string;
  onPick: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="meta-caps text-faint">{label}</span>

      <div className="flex flex-wrap gap-1.5">
        {options.map(([option, name]) => (
          <Chip
            key={option}
            label={name}
            on={value === option}
            attr={option}
            onPick={() => onPick(option)}
          />
        ))}
      </div>
    </div>
  );
}

function Chip({
  label,
  on,
  attr,
  onPick,
}: {
  label: string;
  on: boolean;
  attr: string;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      data-rig-option={attr}
      onClick={onPick}
      className={`meta rounded-[var(--radius-pill)] border px-2.5 py-1 transition-colors ${
        on
          ? 'border-transparent bg-accent text-accent-ink'
          : 'border-line text-soft hover:border-line-strong hover:text-ink'
      }`}
    >
      {label}
    </button>
  );
}

function Swatches({
  label,
  colours,
  value,
  onPick,
}: {
  label: string;
  colours: readonly string[];
  value: string;
  onPick: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="meta-caps text-faint">{label}</span>

      <div className="flex flex-wrap gap-1.5">
        {colours.map((colour) => (
          <button
            key={colour}
            type="button"
            aria-label={colour}
            aria-pressed={value === colour}
            data-rig-colour={colour}
            onClick={() => onPick(colour)}
            style={{ background: colour }}
            className={`h-7 w-7 rounded-[var(--radius-pill)] border-2 transition-transform ${
              value === colour ? 'scale-110 border-ink' : 'border-line hover:scale-105'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function Sheet({
  label,
  items,
  anim,
}: {
  label: string;
  items: { name: string; config: CharacterConfig }[];
  anim: string;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="meta-caps text-faint">
        {label} <span className="text-ghost">· {items.length}</span>
      </h2>

      <Card className="flex flex-wrap items-end gap-x-4 gap-y-4 p-4">
        {items.map(({ name, config }) => (
          <div key={name} className="flex flex-col items-center gap-1" data-rig-cell={name}>
            <Character config={config} size={72} crop="full" anim={anim} />
            <span className="font-mono text-[10px] text-faint">{name}</span>
          </div>
        ))}
      </Card>
    </section>
  );
}
